import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { FormErrors, useFormState } from '../../hooks/useFormState';
import { isBlank, isValidEmail } from '../../utils/validation';
import { buttonClass } from '../../utils/styles';
import { enquiryTypes } from '../../data/formOptions';
import { TextField } from '../form/TextField';
import { SelectField } from '../form/SelectField';
import { TextAreaField } from '../form/TextAreaField';
import { CheckboxField } from '../form/CheckboxField';
import { FormErrorSummary } from '../form/FormErrorSummary';
import { FormPreviewNotice } from '../form/FormPreviewNotice';
import { FormValidatedPanel } from '../form/FormValidatedPanel';
type ContactValues = {
  name: string;
  email: string;
  organization: string;
  enquiryType: string;
  message: string;
  consent: boolean;
  /** Partner routing key — resolved to a private recipient server-side */
  partnerId: string;
  subject: string;
};
const initialValues: ContactValues = {
  name: '',
  email: '',
  organization: '',
  enquiryType: '',
  message: '',
  consent: false,
  partnerId: '',
  subject: ''
};
type ContactFormProps = {
  initialMessage?: string;
  partnerId?: string;
  subject?: string;
  initialEnquiryType?: string;
};
export function ContactForm({
  initialMessage = '',
  partnerId = '',
  subject = '',
  initialEnquiryType = ''
}: ContactFormProps) {
  const formId = 'contact-form';
  const {
    values,
    errors,
    status,
    fieldId,
    setField,
    handleSubmit,
    edit,
    reset
  } = useFormState(formId, {
    ...initialValues,
    message: initialMessage,
    partnerId,
    subject,
    enquiryType: initialEnquiryType
  }, validate);
  if (status === 'validated') {
    return <FormValidatedPanel formId={formId} title="Your message is complete" message={`Thank you, ${values.name.trim().split(' ')[0]}. Your message has passed all checks and is ready to send.`} onEdit={edit} onReset={reset} data-id="element-112" data-mp="3q6alcl0yzcj" />;
  }
  return <form noValidate onSubmit={handleSubmit} className="space-y-6" aria-label="Contact form" data-id="element-113" data-mp="29sjufgncqm6t">
      {status === 'invalid' && <FormErrorSummary count={Object.keys(errors).length} data-id="element-114" data-mp="24quhfvgkjyg3" />}
      <div className="grid gap-6 sm:grid-cols-2" data-id="element-115" data-mp="2ildncgvq1k35">
        <TextField id={fieldId('name')} label="Full name" required autoComplete="name" value={values.name} onChange={v => setField('name', v)} error={errors.name} data-id="element-116" data-mp="2im6pf4jqjley" />
        <TextField id={fieldId('email')} label="Email" type="email" required autoComplete="email" value={values.email} onChange={v => setField('email', v)} error={errors.email} data-id="element-117" data-mp="2nidjo02rr8h2" />
        <TextField id={fieldId('organization')} label="Organization" autoComplete="organization" value={values.organization} onChange={v => setField('organization', v)} data-id="element-118" data-mp="174x40ch1qjb0" />
        <SelectField id={fieldId('enquiryType')} label="Nature of enquiry" required options={enquiryTypes} value={values.enquiryType} onChange={v => setField('enquiryType', v)} error={errors.enquiryType} data-id="element-119" data-mp="t0pth4ax61xe" />
      </div>
      <TextAreaField id={fieldId('message')} label="Message" required value={values.message} onChange={v => setField('message', v)} error={errors.message} data-id="element-120" data-mp="o7wnye7mmpk9" />
      <CheckboxField id={fieldId('consent')} checked={values.consent} onChange={v => setField('consent', v)} error={errors.consent} data-id="element-121" data-mp="ebju9gc30bpi">
        I have read the{' '}
        <Link to="/privacy-policy" className="font-medium text-navy underline underline-offset-2" data-id="element-122" data-mp="5am0rrz9rml6">
          privacy notice
        </Link>{' '}
        and agree to Avenqor Talent Search using these details to respond to my message.
      </CheckboxField>
      <div className="flex flex-col gap-5 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-123" data-mp="3vqzrk6l7o7zq">
        <FormPreviewNotice data-id="element-124" data-mp="3qrvlhotl2qdm" />
        <button type="submit" className={buttonClass('navy', 'shrink-0')} data-id="element-125" data-mp="3qsohbur07vgz">
          Send Message
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-126" data-mp="3lrhbi18f96cy" />
        </button>
      </div>
    </form>;
}
function validate(v: ContactValues): FormErrors<ContactValues> {
  const e: FormErrors<ContactValues> = {};
  if (isBlank(v.name)) e.name = 'Please enter your name.';
  if (isBlank(v.email)) e.email = 'Please enter your email address.';else if (!isValidEmail(v.email)) e.email = 'Please enter a valid email address.';
  if (!v.enquiryType) e.enquiryType = 'Please select the nature of your enquiry.';
  if (isBlank(v.message)) e.message = 'Please enter a message.';else if (v.message.trim().length < 10) e.message = 'Please add a little more detail (at least 10 characters).';
  if (!v.consent) e.consent = 'Please confirm you have read the privacy notice.';
  return e;
}